import { ChangeEvent, FC } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import cleanText from 'utils/cleanText';

import ComposerField from 'modules/ai/components/ComposerField';
import {
  AiEmbedContext,
  EMBED_AI_SUGGESTIONS,
} from 'modules/ai/constants/embedAiSuggestions';
import { useEmbedAiComposer } from 'modules/ai/hooks/useEmbedAiComposer';
import { useEmbedAiIntro } from 'modules/ai/hooks/useEmbedAiIntro';
import { NaviOrbVisual } from 'modules/ai/pages/SocietyAiAgent/components/NaviOrb';
import ThinkingDots from 'modules/ai/pages/SocietyAiAgent/components/ThinkingDots';

import './EmbedFloatingComposer.scss';

type EmbedFloatingComposerProps = {
  context?: AiEmbedContext;
  societyId: string;
  societyName?: string;
  maxSuggestions?: number;
};

const EmbedFloatingComposer: FC<EmbedFloatingComposerProps> = ({
  context = 'society',
  societyId,
  societyName = '',
  maxSuggestions = 4,
}) => {
  const { t } = useTranslate();

  const hasAiAccess = useSelector(
    (state: State) =>
      state.society?.actualSociety?.configuration?.aiPlan?.enabled === true
  );

  const {
    inputText,
    isStreaming,
    selectSuggestion,
    handleSubmit,
    setInputText,
  } = useEmbedAiComposer({ societyId });
  const { rootClassName, handleIntroEnd } = useEmbedAiIntro();

  const suggestionKeys = (
    EMBED_AI_SUGGESTIONS[context] ?? EMBED_AI_SUGGESTIONS.society
  ).slice(0, maxSuggestions);

  if (!hasAiAccess) {
    return null;
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(cleanText(event.target.value));
  };

  return (
    <section
      className={rootClassName}
      data-testid="embed-floating-composer"
      aria-label={t('CHATBOT_Embed_Label')}
    >
      <div className="navi-embed__card" onAnimationEnd={handleIntroEnd}>
        <div className="navi-embed__top-row">
          <div className="navi-embed__brand">
            <NaviOrbVisual size={32} showSparkles isThinking={isStreaming} />
            <span
              className="navi-embed__title-slot"
              role={isStreaming ? 'status' : undefined}
              aria-live={isStreaming ? 'polite' : undefined}
              aria-label={isStreaming ? t('CHATBOT_Thinking') : undefined}
            >
              {isStreaming ? (
                <ThinkingDots />
              ) : (
                <span className="navi-embed__title">
                  {t('CHATBOT_Embed_Title')}
                </span>
              )}
            </span>
          </div>

          <ComposerField
            variant="embed"
            value={inputText}
            onChange={handleChange}
            onSubmit={handleSubmit}
            disabled={isStreaming}
            placeholder={t('CHATBOT_Embed_Placeholder', {
              societyName: societyName || t('Society'),
            })}
            sendAriaLabel={t('CHATBOT_Embed_Send')}
            inputTestId="embed-composer-input"
            sendTestId="embed-composer-send"
          />
        </div>

        {suggestionKeys.length > 0 && (
          <div
            className="navi-embed__suggestions right-aligned"
            role="list"
            aria-label={t('CHATBOT_Embed_SuggestionsLabel')}
          >
            {suggestionKeys.map((key) => (
              <button
                key={key}
                type="button"
                className="navi-embed__chip"
                // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
                role="listitem"
                disabled={isStreaming}
                onClick={() => selectSuggestion(t(key))}
                data-testid={`embed-suggestion-${key}`}
              >
                {t(key)}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default EmbedFloatingComposer;
